import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Legend } from "@similarweb/ui-components";
import {
    LegendTableContainer,
    LegendTableHeader,
    LegendByPositionTableTitle,
    LegendContainer,
    LegendsTableRow,
    TotalKeywords,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/StyledComponents";
import type {
    RankingCompareModeEntity,
    RankingDistributionCompareModeData } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import {
    RankingDistributionMetric,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { RANKING_DISTRIBUTION_DICTIONARY } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionTranslator";
const i18n = i18nFilter();

type CommonLegendsProps = {
    data: RankingDistributionCompareModeData;
    onLegendClick: (legendName: string) => void;
};

const RenderLegendsTable = ({
    data,
    onLegendClick,
    legendsTableTitles,
}: CommonLegendsProps & {
    legendsTableTitles: [string, string];
}) => {
    const getTotalKeywords = (rankingCompareModeEntity: RankingCompareModeEntity) => {
        const totalKeywords = rankingCompareModeEntity.data.reduce((totalKeywordsSoFar, { y }) => {
            return totalKeywordsSoFar + y;
        }, 0);
        return abbrNumberFilter()(totalKeywords);
    };
    return (
        <LegendTableContainer>
            <LegendTableHeader>
                {legendsTableTitles.map((title) => {
                    return <LegendByPositionTableTitle key={title}>{title}</LegendByPositionTableTitle>;
                })}
            </LegendTableHeader>

            {data ? (
                data.map((rankingCompareModeEntity) => {
                    return (
                        <LegendsTableRow key={rankingCompareModeEntity.name}>
                            <Legend
                                key={rankingCompareModeEntity.name}
                                labelColor={rankingCompareModeEntity.color}
                                text={rankingCompareModeEntity.name}
                                isChecked={rankingCompareModeEntity.isActive}
                                onClick={() => onLegendClick(rankingCompareModeEntity.name)}
                            />
                            <TotalKeywords>{getTotalKeywords(rankingCompareModeEntity)}</TotalKeywords>
                        </LegendsTableRow>
                    );
                })
            ) : (
                <>
                    <LegendsTableRow key={0}>
                        <PixelPlaceholderLoader
                            width={"100%"}
                            height={"20px"}
                        />
                    </LegendsTableRow>
                    <LegendsTableRow key={1}>
                        <PixelPlaceholderLoader
                            width={"100%"}
                            height={"20px"}
                        />
                    </LegendsTableRow>
                    <LegendsTableRow key={2}>
                        <PixelPlaceholderLoader
                            width={"100%"}
                            height={"20px"}
                        />
                    </LegendsTableRow>
                    <LegendsTableRow key={3}>
                        <PixelPlaceholderLoader
                            width={"100%"}
                            height={"20px"}
                        />
                    </LegendsTableRow>
                    <LegendsTableRow key={4}>
                        <PixelPlaceholderLoader
                            width={"100%"}
                            height={"20px"}
                        />
                    </LegendsTableRow>
                </>
            )}
        </LegendTableContainer>
    );
};

const RenderLegendsList = ({ data, onLegendClick }: CommonLegendsProps) => {
    const getLegendText = ({
        currentIndex,
        lastItemIndex,
        legendName,
    }: {
        currentIndex: number;
        lastItemIndex: number;
        legendName: string;
    }) => {
        return currentIndex === lastItemIndex
            ? i18n(RANKING_DISTRIBUTION_DICTIONARY.LAST_LEGEND_LIST)
            : i18n(RANKING_DISTRIBUTION_DICTIONARY.TABLE_POSITION, { legendName });
    };
    return (
        <LegendContainer>
            {data &&
                data.map((rankingCompareModeEntity, currentIndex) => {
                    return (
                        <Legend
                            key={rankingCompareModeEntity.name}
                            labelColor={rankingCompareModeEntity.color}
                            text={getLegendText({
                                currentIndex,
                                lastItemIndex: data.length - 1,
                                legendName: rankingCompareModeEntity.name,
                            })}
                            isChecked={rankingCompareModeEntity.isActive}
                            onClick={() => onLegendClick(rankingCompareModeEntity.name)}
                        />
                    );
                })}
        </LegendContainer>
    );
};

type RankingDistributionLegendsUniqueProps = {
    currentMetric: RankingDistributionMetric;
    legendsTableTitles: [string, string];
};

export const RankingDistributionLegends = ({
    data,
    onLegendClick,
    currentMetric,
    legendsTableTitles,
}: CommonLegendsProps & RankingDistributionLegendsUniqueProps) => {
    return currentMetric === RankingDistributionMetric.ByWebsite ? (
        <RenderLegendsList
            data={data}
            onLegendClick={onLegendClick}
        />
    ) : (
        <RenderLegendsTable
            data={data}
            onLegendClick={onLegendClick}
            legendsTableTitles={legendsTableTitles}
        />
    );
};
